import { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { Buildings, MagnifyingGlass, Info } from '@phosphor-icons/react';

export default function OrganizationsPage() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { document.title = 'Radar — Organizations'; }, []);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const r = await api.get('/organizations', { params });
      setOrgs(r.data.organizations || []);
    } catch {}
    setLoading(false);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(fetchOrgs, 200);
    return () => clearTimeout(t);
  }, [fetchOrgs]);

  return (
    <div data-testid="organizations-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#0A0A0A]">Organizations</h1>
          <p className="text-sm text-[#52525B]">Organizations are managed and synchronized from Tychi Master.</p>
        </div>
      </div>

      <div
        className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-md px-4 py-3 mb-4"
        data-testid="orgs-source-banner"
      >
        <Info size={18} className="text-blue-700 shrink-0 mt-0.5" />
        <p className="text-xs text-blue-800 leading-relaxed">
          <strong>Organizations are managed in Tychi Master.</strong> This list is read-only.
          To add, update or remove an organization, please use the Tychi Master console — changes
          will sync automatically to Radar.
        </p>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name…"
            data-testid="orgs-search-input"
            className="w-full pl-9 pr-3 py-2 text-sm border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#0052FF]"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-[#0052FF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : orgs.length === 0 ? (
        <div className="bg-white border border-[#E4E4E7] rounded-md p-12 text-center" data-testid="orgs-empty">
          <Buildings size={48} className="mx-auto text-[#A1A1AA] mb-3" />
          <p className="text-sm text-[#52525B] mb-1">No organizations yet</p>
          <p className="text-xs text-[#A1A1AA]">Organizations will appear here once Tychi Master syncs them.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden" data-testid="orgs-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E4E4E7] bg-[#F7F7F8]">
                <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-[#52525B]">Name</th>
                <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-[#52525B]">Org ID</th>
                <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-[#52525B]">Country</th>
                <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-[#52525B]">Jurisdiction</th>
                <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-[#52525B]">Timezone</th>
                <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-[#52525B]">Created At</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map(o => (
                <tr key={o.id} data-testid={`org-row-${o.id}`} className="border-b border-[#F0F0F1] hover:bg-[#F7F7F8] transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-[#0A0A0A]">{o.name}</td>
                  <td className="px-4 py-3 text-xs font-mono text-[#52525B]" title={o.org_id || o.id}>{o.org_id || o.id}</td>
                  <td className="px-4 py-3 text-sm text-[#52525B]">{o.country || '—'}</td>
                  <td className="px-4 py-3 text-sm text-[#52525B]">{o.jurisdiction || '—'}</td>
                  <td className="px-4 py-3 text-sm text-[#52525B]">{o.timezone || '—'}</td>
                  <td className="px-4 py-3 text-xs text-[#A1A1AA]">
                    {o.created_at ? new Date(o.created_at).toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
